"use client";

import React from "react";
import { Facebook, Instagram, Mail, MapPin, Phone, Twitter } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
function FooterMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-24 bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-3 text-sm text-muted-foreground">
        <li data-index={0}><Link to="/JuniorAcademyPage" className="hover:text-primary transition-colors">Juniors</Link></li>
        <li data-index={1}><Link to="/SeniorFootballPage" className="hover:text-primary transition-colors">Seniors</Link></li>
        <li data-index={2}><Link to="/BusinessHubPage" className="hover:text-primary transition-colors">Business Network</Link></li>
        <li data-index={3}><Link to="/OurLegacyPage" className="hover:text-primary transition-colors">Club History</Link></li>
      </ul>;
  }
  return <ul className="space-y-3 text-sm text-muted-foreground">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="hover:text-primary transition-colors">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted) {
    return <div className="p-3 border border-primary/30 bg-primary/10 rounded-md">
        <p className="text-primary text-sm font-medium">Thank you for subscribing!</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <div className="flex gap-2">
                <Input {...field} type="email" placeholder="Enter your email" className="border-primary/50 focus-visible:ring-primary bg-transparent text-foreground placeholder:text-muted-foreground/70 h-10" />
                <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6 font-semibold">
                  {isSubmitting ? "..." : "Join"}
                </Button>
              </div>
            </FormControl>
            <FormMessage className="text-destructive text-xs mt-1" />
          </FormItem>} />
    </Form>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  const socialLinks = [{
    icon: Instagram,
    href: "https://instagram.com",
    label: "Instagram"
  }, {
    icon: Facebook,
    href: "https://facebook.com",
    label: "Facebook"
  }, {
    icon: Twitter,
    href: "https://twitter.com",
    label: "X (Twitter)"
  }];
  return <section id="footer" className="bg-background text-foreground pt-16 pb-8 px-6 md:px-12 border-t border-border/10">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          
          {}
          <div className="flex flex-col items-start space-y-6">
            <Link to="/" className="inline-block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/wt_birkalla_logo_gold.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/wt_birkalla_logo_gold.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/wt_birkalla_logo_gold.png-wvc-sizes" alt="WT Birkalla Logo" className="h-20 w-auto object-contain" loading="lazy" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
              Fostering community and excellence through development-focused football programs for all ages and skill levels since 1933.
            </p>
          </div>

          {}
          <div className="flex flex-col space-y-6">
            <h3 className="text-lg font-semibold font-default text-foreground tracking-wide uppercase">Quick Links</h3>
            <MenuProvider menu_id="55">
              <FooterMenu />
            </MenuProvider>
          </div>

          {}
          <div className="flex flex-col space-y-6">
            <h3 className="text-lg font-semibold font-default text-foreground tracking-wide uppercase">Contact Us</h3>
            <ul className="space-y-4 text-sm text-muted-foreground">
              <li className="flex items-start gap-3">
                <MapPin className="size-5 text-primary shrink-0 mt-0.5" />
                <span>Birkalla Football Club Grounds, 123 Sports Ave, Birkalla, South Australia</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="size-5 text-primary shrink-0" />
                <span>+61 8 5555 1234</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="size-5 text-primary shrink-0" />
                <span>info@wtbirkalla.com.au</span>
              </li>
            </ul>
          </div>

          {}
          <div className="flex flex-col space-y-6">
            <h3 className="text-lg font-semibold font-default text-foreground tracking-wide uppercase">Stay Updated</h3>
            <p className="text-sm text-muted-foreground">
              Subscribe to our newsletter for the latest club news, fixtures, and events.
            </p>
            <FormProvider formId="footer-newsletter" sectionName="Footer" defaultValues={{
            email: ""
          }}>
              <NewsletterForm />
            </FormProvider>
          </div>

        </div>

        {}
        <div className="pt-8 border-t border-border/20 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground/60">
            &copy; {currentYear} WT Birkalla. All rights reserved.
          </p>
          
          <div className="flex items-center gap-4">
            {socialLinks.map((social, i) => {
            const Icon = social.icon;
            return <div key={i} data-index={i}>
                  <Link to={social.href} newTab className="flex items-center justify-center size-10 rounded-full bg-muted/5 hover:bg-primary/10 text-primary transition-colors duration-300" aria-label={social.label}>
                    <Icon className="size-5" />
                  </Link>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}